/**
 * Виртуальная клавиатура с подсветкой нужной клавиши и пальца.
 *
 * Отрисовывается по данным раскладки, а не по захардкоженной картинке: иначе
 * ЙЦУКЕН, ANSI и ISO пришлось бы поддерживать отдельными макетами (UX-04).
 */

import type { Finger } from '@type2x/shared-contracts';
import { KEY_ROWS, type KeyboardLayout, type KeyDefinition } from '@type2x/keyboard-layouts';

export interface KeyboardViewProps {
  readonly layout: KeyboardLayout;
  /** Символ, который нужно нажать сейчас. */
  readonly nextCharacter: string | null;
  /** Палец, которым набирается следующий символ. */
  readonly nextFinger: Finger | null;
  /** Показывать ли всю клавиатуру или только подсветку зоны. */
  readonly mode: 'full' | 'zone' | 'hidden';
}

function isTarget(key: KeyDefinition, character: string | null): boolean {
  if (character === null) return false;
  return key.base === character.toLowerCase() || key.shifted === character;
}

export function KeyboardView({
  layout,
  nextCharacter,
  nextFinger,
  mode,
}: KeyboardViewProps): React.JSX.Element | null {
  if (mode === 'hidden') return null;

  return (
    <div className="t2x-keyboard" aria-hidden="true">
      {KEY_ROWS.map((row) => {
        const keys = layout.keys.filter((key) => key.row === row);
        if (keys.length === 0) return null;

        return (
          <div key={row} className={`t2x-keyboard__row t2x-keyboard__row--${row}`}>
            {keys.map((key) => {
              const target = isTarget(key, nextCharacter);
              const zone = mode === 'zone' && nextFinger !== null && key.finger === nextFinger;
              const classes = [
                't2x-keyboard__key',
                target ? 't2x-keyboard__key--target' : '',
                zone ? 't2x-keyboard__key--zone' : '',
                mode === 'zone' && !zone && !target ? 't2x-keyboard__key--dimmed' : '',
              ]
                .filter((item) => item !== '')
                .join(' ');

              return (
                <span key={`${row}-${key.column}`} className={classes}>
                  {key.base === ' ' ? '' : key.base}
                </span>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
